import useMountEffect from '@restart/hooks/useMountEffect';
import classNames from 'classnames/bind';
import dayjs from 'dayjs';
import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { RiEditLine, RiDeleteBinLine } from 'react-icons/ri';

import EmptyList from './EmptyList';
import styles from './MissionTable.module.scss';

import ModalSpinner from '@/components/ui/ModalSpinner';
import { getMissions, deleteMission } from '@/helpers/Requester';
import { NotifierService as notifier } from '@/libs/Notifier';

const cx = classNames.bind(styles);

const MissionTable = forwardRef(({ onSelect, showEdit = false, showDelete = false, lazy = false, className }, ref) => {
  const [missions, setMissions] = useState();
  const [selectedMissionId, setSelectedMissionId] = useState();

  useImperativeHandle(ref, () => ({
    load: load,
    clearSelection: () => setSelectedMissionId(null),
  }));

  useMountEffect(() => {
    if (!lazy) {
      load();
    }
  });

  const load = () => {
    getMissions().then(({ success, data }) => {
      if (success) {
        setMissions(data);
      }
    });
  };

  const doSelect = (e) => {
    const nextSelectedMissionId = e.currentTarget.dataset.id;
    setSelectedMissionId(nextSelectedMissionId);

    const found = missions.find(({ id }) => id === nextSelectedMissionId);
    if (found) {
      onSelect(found);
    }
  };

  const moveToEdit = (e) => {
    e.stopPropagation();
    window.open(`/mission-hub/${e.currentTarget.dataset.id}`);
  };

  const doDelete = (e) => {
    e.stopPropagation();
    const id = e.currentTarget.dataset.id;

    deleteMission(id).then(({ success }) => {
      if (success) {
        setMissions(missions.filter((mission) => mission.id !== id));

        // 선택된 미션이 삭제된 경우 미션 선택 해제
        if (selectedMissionId === id) {
          setSelectedMissionId(null);
          onSelect(null);
        }
      } else {
        notifier.error('Failed to delete the mission.');
      }
    });
  };

  return (
    <div className={cx(['container', className])}>
      <div className={cx('header')}>
        <div className={cx(['column', 'no'])}>No</div>
        <div className={cx(['column', 'name'])}>Name</div>
        <div className={cx(['column', 'time'])}>Last Update</div>
        {showEdit && <div className={cx(['column', 'control'])} />}
        {showDelete && <div className={cx(['column', 'control'])} />}
      </div>
      <div className={cx('body')}>
        {!missions && <ModalSpinner />}
        {missions?.length === 0 && (
          <div className={cx('empty')}>
            <EmptyList />
          </div>
        )}
        {missions?.length > 0 && (
          <div className={cx('rows')}>
            {missions.map((mission, index) => {
              const isTarget = mission.id === selectedMissionId;
              const updatedAt = dayjs(mission.updatedAt).format('YYYY-MM-DD HH:mm:ss');

              return (
                <div key={index} data-id={mission.id} onClick={doSelect} className={cx(['row', { target: isTarget }])}>
                  <div className={cx(['column', 'no'])}>{index + 1}</div>
                  <div className={cx(['column', 'name'])}>{mission.name}</div>
                  <div className={cx(['column', 'time'])}>{updatedAt}</div>
                  {showEdit && (
                    <div className={cx(['column', 'control'])}>
                      <button type="button" className={cx('button')} data-id={mission.id} onClick={moveToEdit}>
                        <RiEditLine size={14} color="white" />
                      </button>
                    </div>
                  )}
                  {showDelete && (
                    <div className={cx(['column', 'control'])}>
                      <button type="button" className={cx('button')} data-id={mission.id} onClick={doDelete}>
                        <RiDeleteBinLine size={14} color="white" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
});

export default MissionTable;
